import { api } from "./api";
import { VAPID_PUBLIC_KEY } from "@/config";

const SW_PATH = "/sw.js";
let swReg = null;
let deferredInstall = null;
const installListeners = new Set();

/** Register the PWA service worker (shell cache + push + click handling). */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register(SW_PATH, { scope: "/" }).then((reg) => { swReg = reg; }).catch(() => { /* noop */ });
  });
}

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

/**
 * True web push (needs VAPID key + browser subscription through the relay).
 * Returns true when a subscription was registered with the backend.
 */
export async function enableWebPush() {
  if (!VAPID_PUBLIC_KEY || !("serviceWorker" in navigator) || !("PushManager" in window)) return false;
  const reg = swReg || (await navigator.serviceWorker.ready);
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
  }
  if (!sub) return false;
  await api("/register-push", { method: "POST", body: { platform: "web", device_token: JSON.stringify(sub.toJSON()) } });
  return true;
}

// ---- Install prompt (beforeinstallprompt + iOS instructions) ----
export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export function initInstallPrompt() {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstall = e;
    installListeners.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => { deferredInstall = null; installListeners.forEach((fn) => fn()); });
}
export function onInstallStateChange(fn) { installListeners.add(fn); return () => installListeners.delete(fn); }
export function canInstallNow() { return !!deferredInstall; }

/** Returns "prompted" | "ios" | "installed" | "unavailable". */
export async function installApp() {
  if (isStandalone()) return "installed";
  if (isIos()) return "ios";
  if (deferredInstall) {
    deferredInstall.prompt();
    try { await deferredInstall.userChoice; } catch { /* noop */ }
    deferredInstall = null;
    installListeners.forEach((fn) => fn());
    return "prompted";
  }
  return "unavailable";
}
